import React, { useState, useEffect, useContext } from "react";
import { Alert, Typography, TextField, Button, Grid, Box } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import rnp from "../RNP/rnp.png";
import { ContextSession } from "../../services/ContextSession";
import { isSessionExpiredError, postHeader } from "../../services/http";
import FichaPorNombres from "./fichanombres.component";
import { rnpSubmitButtonSx } from "./rnpButtonStyles";
import "./fichadni.component.css";

const JUSTIFICACION_MAX_LENGTH = 100;

async function getDniData(nombres, primerApellido, segundoApellido, justificacion, token) {
  const json = {
    Nombres: nombres,
    PrimerApellido: primerApellido,
    SegundoApellido: segundoApellido,
    justificacion,
  };
  const auth = `Bearer ${token}`;
  const url = "https://bei360.injupemp.gob.hn:8444/api/rnp/nombre/";
  const datas = await postHeader(url, json, auth);
  if (datas.eror) {
    return null;
  }
  return datas;
}

export default function ConsultaPorNombre() {
  const sessi = useContext(ContextSession);
  const dnida = sessi.getVariable("dniData") || null;
  const [dniData, setDniData] = useState(dnida);
  const [nombres, setNombres] = useState("");
  const [primerApellido, setPrimerApellido] = useState("");
  const [segundoApellido, setSegundoApellido] = useState("");
  const [justificacion, setJustificacion] = useState("");
  const [justificacionError, setJustificacionError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [errorMessage, setErrorMessage] = useState("");
  const [requestError, setRequestError] = useState("");

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      sessi.setVariable("dniData", null);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const getSearchWidth = () => (windowWidth <= 600 ? "300px" : "900px");

  const handleJustificacionChange = (event) => {
    const value = event.target.value.slice(0, JUSTIFICACION_MAX_LENGTH);
    setJustificacion(value);
    if (justificacionError && value.trim()) {
      setJustificacionError(false);
    }
  };

  async function handleConsultar() {
    if (nombres.trim().length === 0 || primerApellido.trim().length === 0) {
      setErrorMessage("Nombres y Primer Apellido son campos obligatorios.");
      return;
    }

    const justificacionLimpia = justificacion.trim();
    if (!justificacionLimpia) {
      setJustificacionError(true);
      return;
    }

    setErrorMessage("");
    setRequestError("");
    setIsLoading(true);

    try {
      const token = sessi.sessionData.token;
      const data = await getDniData(
        nombres.trim(),
        primerApellido.trim(),
        segundoApellido.trim(),
        justificacionLimpia,
        token,
      );
      setDniData(data);
      sessi.setVariable("dniData", data);
    } catch (error) {
      if (isSessionExpiredError(error)) {
        return;
      }

      const message =
        error?.response?.data?.error ||
        error?.payload?.error ||
        error?.data?.error ||
        error?.message ||
        "No se pudo completar la consulta.";
      setRequestError(message);
      setDniData(null);
      sessi.setVariable("dniData", null);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Grid container direction="column" alignItems="center" spacing={2}>
      <Grid item marginTop={3}>
        <Typography variant="h3">Consulta por Nombres</Typography>
      </Grid>
      <Grid item container alignItems="center" justifyContent="center" spacing={2}>
        <Grid item>
          <Typography variant="body1">
            <img src={rnp} alt="RNP Logo" height="70" />
          </Typography>
        </Grid>
        <Grid item>
          <Grid container spacing={2} style={{ width: getSearchWidth() }}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                required
                id="nombres"
                label="Nombres"
                variant="outlined"
                value={nombres}
                onChange={(e) => setNombres(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                required
                id="primerapellido"
                label="Primer Apellido"
                variant="outlined"
                value={primerApellido}
                onChange={(e) => setPrimerApellido(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                id="segundoapellido"
                label="Segundo Apellido"
                variant="outlined"
                value={segundoApellido}
                onChange={(e) => setSegundoApellido(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                maxRows={3}
                id="justificacion-input"
                label="Justificación breve"
                variant="outlined"
                value={justificacion}
                onChange={handleJustificacionChange}
                inputProps={{ maxLength: JUSTIFICACION_MAX_LENGTH }}
                error={justificacionError}
                helperText={
                  justificacionError
                    ? "Debe indicar la justificación de la consulta."
                    : `Motivo laboral de la consulta. ${justificacion.length}/${JUSTIFICACION_MAX_LENGTH}`
                }
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item>
          <Button
            onClick={handleConsultar}
            disabled={isLoading || !justificacion.trim()}
            variant="contained"
            sx={rnpSubmitButtonSx}
          >
            Enviar
          </Button>
        </Grid>
      </Grid>
      {errorMessage && (
        <Grid item>
          <Alert severity="warning">{errorMessage}</Alert>
        </Grid>
      )}
      {requestError && (
        <Grid item>
          <Alert severity="warning">{requestError}</Alert>
        </Grid>
      )}
      <Grid item>{isLoading && <CircularProgress color="success" />}</Grid>
      <Grid item>{isLoading && <Typography>Cargando...</Typography>}</Grid>
      {dniData != null && (
        <Grid>
          <Box>
            {dniData.error == null && dniData.error == undefined && (
              <FichaPorNombres dniData={dniData} justificacion={justificacion.trim()} />
            )}
            {dniData.error && (
              <div className="ficha">
                <div className="datos-container">
                  <div className="datos">
                    <div className="seccion">
                      <h2>Resultados De Búsqueda</h2>
                      <table>
                        <tbody>
                          <tr>
                            <td className="valor1" align="center">
                              No se encontraron datos para ese nombre.
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </Box>
        </Grid>
      )}
    </Grid>
  );
}
